/**
 * "I paid but I have no access", answered without switching tabs.
 *
 * Compares what TipRanks grants against what the payment providers report. Both
 * sides are already fetched; this only says out loud when they disagree.
 *
 * Deliberately conservative — a false alarm on a normal account trains people
 * to ignore the banner:
 * - A manually granted plan (employee, compensation) has no provider
 *   subscription by design, so a plan alone is never a finding. Only a plan that
 *   *claims* a payment provider and has none is.
 * - A lapsed provider subscription on an expired plan is just a churned user.
 */
import type { BillingSnapshot, UserAccount } from "./user-management.js";

export type Disagreement = {
  severity: "warning" | "info";
  headline: string;
  detail: string;
};

const ACTIVE_STATES = ["active", "trialing", "past_due"];

function isLive(status: string): boolean {
  return ACTIVE_STATES.includes(status.toLowerCase());
}

/** Plan names that grant nothing paid. Upstream says "Free" or "Basic". */
const FREE_PLANS = ["basic", "free", "none", ""];

export function isPaidPlan(name: string | null): boolean {
  return name != null && !FREE_PLANS.includes(name.trim().toLowerCase());
}

/**
 * Upstream writes "None" where it means no provider (same as `tag`), so a bare
 * null check is not enough — that string is why a free account was once told
 * TipRanks expected a payment.
 */
export function providerOrNull(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim();
  return v === "" || v.toLowerCase() === "none" ? null : v;
}

export function findDisagreements(
  account: UserAccount,
  snapshots: BillingSnapshot[]
): Disagreement[] {
  // Providers we could not ask tell us nothing — never infer from silence.
  const answered = snapshots.filter((s) => s.available);
  if (answered.length === 0) return [];

  const liveSubs = answered.flatMap((s) =>
    s.subscriptions.filter((sub) => isLive(sub.status)).map((sub) => ({ s, sub }))
  );

  const planActive =
    isPaidPlan(account.productPlanName) &&
    (account.expiration == null || new Date(account.expiration) > new Date());

  const out: Disagreement[] = [];

  // The money case: someone is being charged and has nothing to show for it.
  if (liveSubs.length > 0 && !planActive) {
    const { s, sub } = liveSubs[0];
    out.push({
      severity: "warning",
      headline: `Paying at ${s.provider}, but TipRanks shows no active plan`,
      detail:
        `${s.provider} has a live subscription (${sub.product ?? sub.id}` +
        `${sub.nextChargeDate ? `, next charge ${sub.nextChargeDate}` : ""}) while TipRanks ` +
        `shows ${account.productPlanName ?? "no plan"}` +
        `${account.expiration ? ` expiring ${account.expiration.slice(0, 10)}` : ""}. ` +
        `The user is being charged for access they do not have.`,
    });
  }

  // The mirror case: we grant a paid-looking plan nobody is paying for.
  const claimsProvider =
    providerOrNull(account.nextChargePaymentProvider) != null ||
    account.activeSubscriptions.some(
      (s) => providerOrNull(s.paymentProvider) != null
    );

  if (planActive && claimsProvider && liveSubs.length === 0) {
    out.push({
      severity: "warning",
      headline: "TipRanks expects a payment no provider is making",
      detail:
        `TipRanks shows ${account.productPlanName} billed via ` +
        `${providerOrNull(account.nextChargePaymentProvider) ?? "a payment provider"}, but no provider ` +
        `reports a live subscription for any address on this account. ` +
        `Either the subscription was cancelled without the plan being ended, or it lives ` +
        `under an address we do not know about.`,
    });
  }

  // Worth seeing, not alarming: more than one live subscription is how
  // double-charging shows up.
  if (liveSubs.length > 1) {
    out.push({
      severity: "info",
      headline: `${liveSubs.length} live subscriptions across the providers`,
      detail:
        liveSubs
          .map(({ s, sub }) => `${s.provider}: ${sub.product ?? sub.id}`)
          .join(" · ") + ". Check this is not a double charge.",
    });
  }

  return out;
}
